import { useCallback } from 'react';

import { useSearchParams } from 'react-router-dom';
import { todayWallClockDate } from '@/utils/wallClock';

export interface ImplantSession {
  impDate: string;
  siteId: string;
  yardId: string;
  vetId: string;
  techId: string;
  chuteNumber: number;
  clientRecipMode: boolean;
  caneSearch: string;
  setImpDate: (date: string) => void;
  setSiteId: (id: string) => void;
  setYardId: (id: string) => void;
  setVetId: (id: string) => void;
  setTechId: (id: string) => void;
  setChuteNumber: (chute: number) => void;
  setClientRecipMode: (on: boolean) => void;
  setCaneSearch: (cane: string) => void;
}

function defaultDate(): string {
  return todayWallClockDate();
}

export function useImplantSession(): ImplantSession {
  const [searchParams, setSearchParams] = useSearchParams();

  const impDate = searchParams.get('impDate') || defaultDate();
  const siteId = searchParams.get('siteId') || searchParams.get('site') || '';
  const yardId = searchParams.get('yardId') || searchParams.get('yard') || '';
  const vetId = searchParams.get('vetId') || searchParams.get('vet') || '';
  const techId = searchParams.get('techId') || searchParams.get('tech') || '';
  const chuteNumber =
    Number(searchParams.get('chuteNumber')) ||
    Number(searchParams.get('chute')) ||
    1;
  const clientRecipMode = searchParams.get('clientRecip') === '1';
  const caneSearch = searchParams.get('cane') || '';

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return {
    impDate,
    siteId,
    yardId,
    vetId,
    techId,
    chuteNumber,
    clientRecipMode,
    caneSearch,
    setImpDate: (date: string) => updateParam('impDate', date),
    setSiteId: (id: string) => updateParam('siteId', id),
    setYardId: (id: string) => updateParam('yardId', id),
    setVetId: (id: string) => updateParam('vetId', id),
    setTechId: (id: string) => updateParam('techId', id),
    setChuteNumber: (chute: number) =>
      updateParam('chuteNumber', chute.toString()),
    setClientRecipMode: (on: boolean) =>
      updateParam('clientRecip', on ? '1' : ''),
    setCaneSearch: (cane: string) => updateParam('cane', cane),
  };
}
